import { Shield, User, Clock, Key, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

function hopLabel(hop, index, chain) {
  const senderName = hop.sender_name || (hop.sender === 'OWNER' ? 'Document Owner' : hop.sender || 'Unknown');
  const receiverName = hop.receiver_name || hop.requester_name || hop.recipient_name || hop.receiver || 'Unknown';
  if (index === 0) {
    return 'Document uploaded';
  }
  return `Shared from ${senderName} to ${receiverName}`;
}

export default function ChainVisualization({
  chain,
  udtId,
  canRevoke = false,
  onRevokeRecipient,
  ownerId,
  isDeletedView = false,
}) {
  if (!chain || chain.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Custody Chain</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-center py-4" style={{ color: '#666' }}>No chain history available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-w-0">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base sm:text-lg">Document Custody Chain</CardTitle>
          <Badge style={{ background: '#0F52BA' }} className="text-xs sm:text-sm">{chain.length} Hops</Badge>
        </div>
        <p className="text-xs sm:text-sm mt-1" style={{ color: '#666' }}>Complete transfer history with watermark tracking</p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0">
        <div className="space-y-4 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto overflow-x-hidden pr-1 min-w-0">
          <div className="relative">
            {chain.map((hop, index) => (
              <div key={hop.hop_id || index} className="relative pb-6 last:pb-0">
                {index < chain.length - 1 && (
                  <div
                    className="absolute left-3 sm:left-4 top-10 bottom-0 w-0.5"
                    style={{ background: 'linear-gradient(to bottom, #0F52BA, #60A5FA)' }}
                  />
                )}
                <div className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="relative z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: index === 0 ? '#10b981' : '#0F52BA', color: 'white' }}
                  >
                    {index === 0 ? <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                  </div>
                  <div className="flex-1 min-w-0 bg-white border rounded-lg p-3 sm:p-4" style={{ borderColor: '#E8F2FF' }}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex flex-col gap-1 min-w-0">
                        <p className="font-semibold text-sm sm:text-base text-gray-900 break-words">
                          {hopLabel(hop, index, chain)}
                        </p>
                        {hop.receiver_name && (
                          <p className="text-xs text-gray-500 break-words">
                            Recipient: {hop.receiver_name}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 flex-shrink-0">
                        {index === 0 && <Badge className="bg-green-100 text-green-800">Origin</Badge>}
                        {index === chain.length - 1 && !isDeletedView && (
                          <Badge className="bg-blue-100 text-blue-800">Current</Badge>
                        )}
                        {index === chain.length - 1 && isDeletedView && (
                          <Badge className="bg-red-100 text-red-800">Deleted</Badge>
                        )}
                        {hop.recipient_status === 'revoked' && (
                          <Badge className="bg-red-100 text-red-800">Revoked</Badge>
                        )}
                        {/* Revoke is owner-only: canRevoke is only set when the viewer is the document owner */}
                        {canRevoke && !isDeletedView && onRevokeRecipient && hop.receiver && hop.receiver !== ownerId && hop.recipient_status !== 'revoked' && (
                          <button
                            type="button"
                            onClick={() => onRevokeRecipient(hop.receiver)}
                            className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-3 h-3" />
                            Revoke access
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-gray-500 flex-wrap">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="break-all">{new Date(hop.timestamp).toLocaleString()}</span>
                    </div>
                    {hop.encryption_key_fingerprint && (
                      <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-gray-500 min-w-0">
                        <Key className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <code className="text-xs px-2 py-0.5 rounded bg-gray-100 break-all">{hop.encryption_key_fingerprint}</code>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Summary */}
          <div className="mt-6 p-3 sm:p-4 rounded-lg min-w-0" style={{ background: '#E8F2FF' }}>
            <p className="text-xs sm:text-sm font-semibold mb-2" style={{ color: '#0F52BA' }}>Chain Summary</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              <div>
                <span style={{ color: '#666' }}>Total Hops:</span>
                <span className="ml-2 font-semibold">{chain.length}</span>
              </div>
              {!isDeletedView && (
                <div>
                  <span style={{ color: '#666' }}>Current Holder:</span>
                  <span className="ml-2 font-semibold">
                    {chain[chain.length - 1]?.receiver_name ||
                      chain[chain.length - 1]?.requester_name ||
                      chain[chain.length - 1]?.recipient_name ||
                      'Owner'}
                  </span>
                </div>
              )}
              <div>
                <span style={{ color: '#666' }}>Origin:</span>
                <span className="ml-2 font-semibold">
                  {new Date(chain[0]?.timestamp).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span style={{ color: '#666' }}>
                  {isDeletedView ? 'Deleted:' : 'Last Transfer:'}
                </span>
                <span className="ml-2 font-semibold">
                  {new Date(chain[chain.length - 1]?.timestamp).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
